import apiClient from '../apiClient';
import type {
    SarCaseListResult,
    SarCaseDetail,
    SarCaseQuery,
    SarReporting,
    SarManualTransmit,
    SarReportingOutcome,
    SarRegulationPublic
} from '@/types/sarCase';
import type { SarDashboardResponse } from '@/types/sarDashboard';

export default {
    getCases: async (q: SarCaseQuery): Promise<SarCaseListResult> => {
        return apiClient.get('/sar-cases/', {
            params: {
                entity_type: q.entityType,
                tax_id_basic: q.taxIdBasic,
                report_type: q.reportType,
                scope: q.scope,
                entity_tax_id: q.entityTaxId || undefined,
                display_id: q.displayId || undefined,
                entity_types: q.entityTypes?.length ? q.entityTypes : undefined,
                regulators: q.regulators?.length ? q.regulators : undefined,
                stages: q.stages?.length ? q.stages : undefined,
                assigned_to_me: q.assignedToMe || undefined,
                date_from: q.dateFrom || undefined,
                date_to: q.dateTo || undefined,
                closed_from: q.closedFrom || undefined,
                closed_to: q.closedTo || undefined,
                closed_reason: q.closedReasons?.length ? q.closedReasons : undefined,
                page: q.page,
                rows: q.rows
            }
        });
    },

    getCase: async (id: string): Promise<SarCaseDetail> => {
        return apiClient.get(`/sar-cases/${id}`);
    },

    // Aggregated indicators for the global SAR Painel (COS, trailing 30 days). No filters —
    // the panel is a fixed-window overview.
    getDashboard: async (): Promise<SarDashboardResponse> => {
        return apiClient.get('/sar-cases/dashboard');
    },

    // Regulators a case can be opened under (each maps to one sector; sector + codes
    // are derived server-side), each enriched with its obligation's legal basis + official
    // source links. Drives the create-SAR picker + its "o que se reporta" panel.
    getReportOptions: async (
        family = 'suspicious',
        country = 'br',
    ): Promise<SarRegulationPublic[]> => {
        return apiClient.get('/sar-cases/report-options', { params: { family, country } });
    },

    // Open a new SAR case (COS). Sector, format and occurrence codes are resolved from
    // the chosen regulator on the backend.
    createCase: async (payload: {
        report_type_family: string;
        country_code?: string;
        entity_type: 'person' | 'company';
        entity_tax_id: string;
        entity_name: string;
        entity_tax_id_basic?: string;
        regulator_slug: string;
        assigned_user_id?: string;
        operation_window_start?: string;
        operation_window_end?: string;
    }): Promise<SarCaseDetail> => {
        return apiClient.post('/sar-cases/', payload);
    },

    // COS deliberation move (draft → ready_to_report | dismissed | …).
    // `dismissal_rationale` is captured on the dismiss action itself.
    transition: async (
        id: string,
        payload: {
            to_stage_slug: string;
            comment?: string;
            new_assignee_id?: string;
            dismissal_rationale?: string;
        }
    ): Promise<SarCaseDetail> => {
        return apiClient.post(`/sar-cases/${id}/transition`, payload);
    },

    // Persist a differential patch of the COS deliberation fields (committee decision +
    // rationale, responsible sign-off) onto the workflow ticket.
    saveDecision: async (id: string, patch: Record<string, any>): Promise<SarCaseDetail> => {
        return apiClient.patch(`/sar-cases/${id}/decision`, patch);
    },

    // Reassign the current owner without changing stage.
    updateAssignee: async (id: string, assignedUserId: string | null): Promise<SarCaseDetail> => {
        return apiClient.patch(`/sar-cases/${id}/assignee`, { assigned_user_id: assignedUserId });
    },

    getActions: async (id: string): Promise<any[]> => {
        return apiClient.get(`/sar-cases/${id}/actions`);
    },

    // COS deliberation stages (for the nested status track)
    getStages: async (): Promise<any[]> => {
        return apiClient.get('/sar-cases/stages');
    },

    // Auto-save a single report-body field on blur (draft only). Merges onto the living
    // body server-side. `field_name` null → whole-section patch (table rows array).
    saveReportField: async (
        id: string,
        payload: { section_key: string; field_name: string | null; value: any }
    ): Promise<SarCaseDetail> => {
        return apiClient.patch(`/sar-cases/${id}/report-field`, payload);
    },

    // Manual filing: record a communication the analyst filed on SISCOAF by hand. Creates
    // the reporting + attempt in one step, moves the case READY → FILED, and captures the
    // Número COAF (SISCOAF assigns it at filing time).
    transmit: async (payload: SarManualTransmit): Promise<SarReporting> => {
        return apiClient.post('/sar-cases/reportings/transmit', payload);
    },

    // Suggested next numero de origem (our control number) for the transmit dialog — a
    // non-reserving hint; the analyst may override it and it's finalized at transmit.
    nextOriginReference: async (): Promise<{ origin_reference: string }> => {
        return apiClient.get('/sar-cases/next-origin-reference');
    },

    // Live availability check for the transmit dialog's Nº de origem field. Returns whether the
    // value is still free in the account plus the current auto-suggested next number. A hint only —
    // the authoritative uniqueness check runs inside the transmit transaction.
    checkOriginReference: async (
        value: string
    ): Promise<{ value: string; available: boolean | null; suggestion: string }> => {
        return apiClient.get('/sar-cases/origin-reference/check', { params: { value } });
    },

    // Fetch the case's report body as structured SISCOAF XML (best-effort, not XSD-validated),
    // returning the blob plus the server-provided filename. Caller triggers the download.
    fetchReportXml: async (id: string): Promise<{ blob: Blob; filename: string }> => {
        const res: Response = await apiClient.get(`/sar-cases/${id}/report.xml`, { rawResponse: true } as any);
        const blob = await res.blob();
        const disposition = res.headers.get('content-disposition') ?? '';
        const filename = /filename="?([^"]+)"?/.exec(disposition)?.[1] ?? `comunicacao_${id}.xml`;
        return { blob, filename };
    },

    // Record the COAF verdict (accepted / rejected) read back later from SISCOAF.
    recordOutcome: async (
        reportingId: string,
        payload: SarReportingOutcome
    ): Promise<SarReporting> => {
        return apiClient.post(`/sar-cases/reportings/${reportingId}/outcome`, payload);
    },

    // Return a COAF-rejected case to READY so it can be corrected and re-transmitted
    // (the resend is filed as a rectification). `toStageSlug` is the chosen config action.
    reopen: async (id: string, toStageSlug: string): Promise<SarCaseDetail> => {
        return apiClient.post(`/sar-cases/${id}/reopen`, { to_stage_slug: toStageSlug });
    },
};
